import { useEffect, useRef } from 'react'
import { useChatStore } from '../../stores/useChatStore.js'
import { MessageBubble } from './MessageBubble.js'
import { Spinner } from '../../components/index.js'
import type { ChatMessage } from '../../types/chat.js'
import type { UserProfile } from '../../types/user.js'

interface MessageListProps {
  chatId: string
  messages: ChatMessage[]
  currentUserId: number
  users: UserProfile[]
  isLoading: boolean
  onEdit: (messageId: string, content: string) => void
  onDelete: (messageId: string) => void
}

export function MessageList({
  chatId,
  messages,
  currentUserId,
  users,
  isLoading,
  onEdit,
  onDelete,
}: MessageListProps) {
  const bottomRef = useRef<HTMLDivElement>(null)
  const prevLengthRef = useRef(messages.length)
  const getMessageReadCount = useChatStore((s) => s.getMessageReadCount)

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (messages.length > prevLengthRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
    prevLengthRef.current = messages.length
  }, [messages.length])

  // Scroll to bottom on first load
  useEffect(() => {
    bottomRef.current?.scrollIntoView()
  }, [])

  function getUserName(userId: number): string {
    return users.find((u) => u.id === userId)?.name ?? 'Usuário'
  }

  function getUserAvatar(userId: number): string | null {
    return users.find((u) => u.id === userId)?.avatarUrl ?? null
  }

  function shouldShowSender(index: number): boolean {
    if (index === 0) return true
    const prev = messages[index - 1]
    const curr = messages[index]
    return prev.userId !== curr.userId
  }

  return (
    <div className="flex-1 overflow-y-auto py-2">
      {isLoading && (
        <div className="flex justify-center py-4">
          <Spinner size="sm" />
        </div>
      )}

      {messages.map((msg, i) => (
        <MessageBubble
          key={msg.id}
          message={msg}
          isMine={msg.userId === currentUserId}
          senderName={getUserName(msg.userId)}
          senderAvatar={getUserAvatar(msg.userId)}
          showSender={shouldShowSender(i)}
          readCount={Math.max(msg.readByCount ?? 0, getMessageReadCount(chatId, msg.id))}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}

      <div ref={bottomRef} />
    </div>
  )
}
